import { useState, useEffect } from 'react';
import { translations } from '@/utils/translations';

const CountdownTimer = ({ targetDate, eventColor = '#00F0FF', timezoneOffset = 0, language = 'de' }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const t = translations[language] || translations['de'];

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      
      const targetYear = targetDate.getFullYear();
      const targetMonth = targetDate.getMonth();
      const targetDay = targetDate.getDate();
      const targetHour = targetDate.getHours();
      const targetMinute = targetDate.getMinutes();
      const targetSecond = targetDate.getSeconds();
      
      const targetUTC = Date.UTC(targetYear, targetMonth, targetDay, targetHour, targetMinute, targetSecond);
      const targetInTimezone = targetUTC - (timezoneOffset * 3600000);
      
      const difference = targetInTimezone - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate, timezoneOffset]);

  const formatNumber = (num) => String(num).padStart(2, '0');

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div 
        className="countdown-digit w-20 h-24 sm:w-28 sm:h-32 flex items-center justify-center rounded-lg animate-pulse-glow"
        style={{ 
          boxShadow: `0 0 20px ${eventColor}30, inset 0 0 20px ${eventColor}10`,
          borderColor: `${eventColor}50`
        }}
      >
        <span 
          className="font-mono text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter"
          style={{ color: eventColor }}
          data-testid={`countdown-${label.toLowerCase()}`}
        >
          {formatNumber(value)}
        </span>
      </div>
      <span className="font-rajdhani text-xs uppercase tracking-[0.2em] text-slate-500 mt-3">
        {label}
      </span>
    </div>
  );

  const Separator = () => (
    <div className="flex flex-col items-center justify-center h-24 sm:h-32">
      <div 
        className="w-2 h-2 rounded-full mb-2"
        style={{ backgroundColor: eventColor, boxShadow: `0 0 10px ${eventColor}` }}
      />
      <div 
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: eventColor, boxShadow: `0 0 10px ${eventColor}` }}
      />
    </div>
  );

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-4" data-testid="countdown-timer">
      <TimeBlock value={timeLeft.days} label={t.days} />
      <Separator />
      <TimeBlock value={timeLeft.hours} label={t.hours} />
      <Separator />
      <TimeBlock value={timeLeft.minutes} label={t.minutes} />
      <Separator />
      <TimeBlock value={timeLeft.seconds} label={t.seconds} />
    </div>
  );
};

export default CountdownTimer;
